import { injectable, decorate, inject } from 'inversify';
import 'reflect-metadata';
import qs from 'query-string';
import { SERVICE_IDENTIFIER } from 'constants/identifiers';
import { API_URL } from 'constants/env';
import { ServiceError } from 'errors';
import { authStore } from 'stores';
import { handleServiceError } from '../utils/errors';

interface HttpService {
  axiosInstance: {
    interceptors: {
      response: {
        use: (
          onFulfilled: (response: unknown) => unknown,
          onRejected: (error: unknown) => Promise<never> | true,
        ) => unknown;
      };
    };
  };
  setConfig: (config: ApiConfig) => void;
  get: (
    route: string,
    params?: RequestData,
    axiosConfig?: AxiosConfig,
  ) => Promise<unknown>;
  post: (
    route: string,
    data?: RequestData | null,
    axiosConfig?: AxiosConfig,
  ) => Promise<unknown>;
  patch: (
    route: string,
    data?: RequestData | null,
    axiosConfig?: AxiosConfig,
  ) => Promise<unknown>;
  put: (
    route: string,
    data?: RequestData | null,
    axiosConfig?: AxiosConfig,
  ) => Promise<unknown>;
  delete: (
    route: string,
    params?: RequestData,
    axiosConfig?: AxiosConfig,
  ) => Promise<unknown>;
}

type RequestData = Record<string, unknown>;
type AxiosConfig = Record<string, unknown>;

interface ApiConfig extends AxiosConfig {
  baseURL: string;
  withCredentials: boolean;
  paramsSerializer: (params: RequestData) => string;
}

interface HttpError {
  response?: {
    status?: number;
  };
  message?: string;
}

class ApiService {
  private httpService: HttpService;

  constructor(httpService: HttpService) {
    this.httpService = httpService;

    const config = {
      baseURL: API_URL,
      withCredentials: true,
      paramsSerializer: (params: RequestData): string => {
        return qs.stringify(params);
      },
    };

    this.httpService.setConfig(config);
    this.httpService.axiosInstance.interceptors.response.use(
      (response: unknown) => response,
      (error: unknown) => {
        const httpError = error as HttpError;
        if (httpError.response?.status === 401) {
          authStore.signOut();
        }
        if (httpError.message !== 'canceled') {
          const serviceError = new ServiceError(error);
          return Promise.reject(serviceError);
        }
        return true;
      },
    );
  }

  async get(
    route: string,
    params: RequestData = {},
    axiosConfig: AxiosConfig = {},
  ): Promise<unknown> {
    try {
      return await this.httpService.get(route, params, axiosConfig);
    } catch (e) {
      handleServiceError(e);
      throw e;
    }
  }

  async post(
    route: string,
    data: RequestData | null = null,
    axiosConfig: AxiosConfig = {},
  ): Promise<unknown> {
    try {
      const result = await this.httpService.post(route, data, axiosConfig);
      return result;
    } catch (e) {
      if (
        route.indexOf('accept_card') < 0 &&
        route.indexOf('card_didnt_match') < 0 &&
        route.indexOf('new_meeting_objects') < 0 &&
        route.indexOf('payment_calendar') < 0
      )
        handleServiceError(e);
      throw e;
    }
  }

  async patch(
    route: string,
    data: RequestData | null = null,
    axiosConfig: AxiosConfig = {},
  ): Promise<unknown> {
    try {
      return await this.httpService.patch(route, data, axiosConfig);
    } catch (e) {
      handleServiceError(e);
      throw e;
    }
  }

  async put(
    route: string,
    data: RequestData | null = null,
    axiosConfig: AxiosConfig = {},
  ): Promise<unknown> {
    try {
      return await this.httpService.put(route, data, axiosConfig);
    } catch (e) {
      handleServiceError(e);
      throw e;
    }
  }

  async delete(
    route: string,
    params: RequestData = {},
    axiosConfig: AxiosConfig = {},
  ): Promise<unknown> {
    try {
      return await this.httpService.delete(route, params, axiosConfig);
    } catch (e) {
      handleServiceError(e);
      throw e;
    }
  }
}

(decorate as (decorator: ClassDecorator, target: unknown) => void)(
  injectable() as ClassDecorator,
  ApiService,
);
(
  decorate as (
    decorator: ParameterDecorator,
    target: unknown,
    parameterIndex: number,
  ) => void
)(inject(SERVICE_IDENTIFIER.HttpService) as ParameterDecorator, ApiService, 0);

export default ApiService;
